import { defineStore } from 'pinia';
import { ref } from 'vue';
import { OCheckboxSigmaItem } from 'ofe-common/components';
import useAnalytics from '@globals/composables/analytics';
import serverFetch from '@globals/ServerFetch';

import { FormGroupItem } from '@globals/types/components/FormGroup';
import { NipValue } from '@globals/types/components/NipInput';
import {
	BackendCategorySuggestion,
	RegisterCategory,
	RegisterLocation,
	RegisterSentForm,
} from '@public/types/Register';

export interface AdditionalCatItem extends OCheckboxSigmaItem {
	addedByUser?: boolean;
}

export const useRegisterStore = defineStore('register', () => {
	const state = {
		/**
 		 * Kod polecający, który mógł zostać przekazany w parametrze URL.
		 */
		referralCode: ref(''),

		/**
		 * Dane dla ekranu potwierdzającego rejestrację (ten z kopertką)
		 */
		sentForm: ref<RegisterSentForm>({
			email: '',
			companyName: '',
			firstName: '',
			customerId: -1,
		}),

		/**
		 * Informacja o liczbie złożonych zapytań w danej kategorii lub lokalizacji,
		 * któraj jest pokazywana na pierwszym kroku rejestracji.
		 */
		lastMonthRfpCount: ref(0),

		/**
		 * Czy użytkownik trafił na czarną listę?
		 */
		isBlacklisted: ref(''),

		/**
		 * Jeżeli wejście na rejestrację nastąpiło z zapytania ofertowego,
		 * w parametrze URL przekazany jest identyfikator tego zapytania.
		 */
		initRfpId: ref(0),

		/**
		 * Jeżeli wejście na rejestrację miało przypisaną kategorię w parametrze,
		 * zamieniamy tło strony na odpowiadające tej kategorii.
		 */
		backgroundUrl: ref(''),

		/**
		 * Formularz ostatniego kroku rejestracji, zawierający dane kontaktowe.
		 */
		contactForm: ref<FormGroupItem[]>([]),

		/**
		 * Lista kategorii dodatkowych do wyboru na opcjonalnym drugim kroku rejestracji.
		 */
		additionalCatsList: ref<AdditionalCatItem[]>([]),

		/**
		 * Czy lista dodatkowych kategorii została już pobrana?
		 */
		additionalCatsLoaded: ref(false),

		/**
		 * Zaznaczone kategorie dodatkowe
		 */
		selectedAdditionalCats: ref<string[]>([]),

		/**
		 * Zaznaczone lokalizacje dodatkowe
		 */
		selectedAdditionalLocs: ref<string[]>([]),

		/**
		 * Czy inicjalne załadowanie strony zakończyło się?
		 */
		isLoading: ref(true),

		/**
		 * Wybrana na pierwszym kroku lokalizacja.
		 */
		registerLocation: ref<RegisterLocation>({
			value: ['', ''],
			autocomplete: 'Location',
			suggestions: [],
			countryMode: {
				enabled: false,
				items: [],
				value: '',
			},
		}),

		/**
		 * Wybrana na pierwszym kroku kategoria.
		 */
		registerCategory: ref<RegisterCategory>({
			value: ['', ''],
			categoryId: {
				level2: 0, level3: 0, parent: 0, id: 0,
			},
			autocomplete: '/common/index/autocomplete-form-registration.ofer?onlyLeafsInGeneralView=true&query={{q}}&includeAdditionalDetails=true',
			suggestions: [],
			showSuggestions: false,
			isLeaf: true,
			customPlaceholder: '',
		}),

		/**
		 * Oryginalny formularz (do wysłania kontaktu na czarną listę)
		 */
		originalForm: ref({} as Record<string, string>),

		/**
		 * Dopisek przy kategoriach sprzedażowych
		 */
		notServiceLabel: ref(` <small class="mx-1 text-neutral-600">Sprzedaż</small>`),
	};

	const actions = {
		/**
		 * Wysyła informacje analityczne do Google Tag Manager.
		 * @param eventName Nazwa zdarzenia po prefiksie `registration_`.
		 */
		sendAnalytics(eventName: string) {
			const analytics = useAnalytics();
			const cat = state.registerCategory.value;
			console.log('sendAnalytics', eventName, cat.value[0]);
			analytics.sendGAEvent(`registration_${eventName}`, {
				category_id: cat.value[0],
				category_name: cat.value[1],
				category_parent: cat.categoryId.parent || '',
				category_level_2: cat.categoryId.level2 || '',
				category_level_3: cat.categoryId.level3 || '',
				is_redesigned: 'b',
			});
		},

		/**
		 * Pobiera kategorie dodatkowe z endpoint-a `get-related-categories.ofer`.
		 * @param categoryId Identyfikator kategorii.
		 */
		async setAdditionalCats(categoryId: number) {
			// Pobierz i wyświetl kategorie dodatkowe, jeżeli nie zostały jeszcze pobrane
			state.additionalCatsList.value = [];
			state.selectedAdditionalCats.value = [];
			const { relatedCategories } = await serverFetch(`/register/get-related-categories.ofer?mainCat=${categoryId}`);

			// Pomiń krok, jeżeli nie ma żadnych kategorii dodatkowych do pokazania
			if (!(relatedCategories || []).length) { return false; }

			state.additionalCatsList.value = relatedCategories.map((cat: BackendCategorySuggestion) => ({
				value: String(cat.idCategory),
				text: cat.categoryName + ((!cat.service) ? state.notServiceLabel.value : ''),
			}));

			state.additionalCatsLoaded.value = true;
			return true;
		},

		/**
		 * Przesyła informacje o kategorii i lokalizacji z parametru URL
		 * do wywołania endpoint-a `get-register-info.ofer`, a następnie
		 * ustawia pola na pierwszym kroku formularza rejestracji.
		 * @param category Identyfikator kategorii.
		 * @param location Identyfikator lokalizacji.
		 */
		async setInfoFromUrlParams(category = 0, location = '') {
			const params = [];
			if (!category && !location) { return; }

			// Kategorie 2 i 198 zostaną usunięte, ale ten warunek na razie zostaje, na wszelki wypadek
			if (category > 2 && category !== 198) { params.push(`idCategory=${category}`); }
			if (location) { params.push(`locationName=${location}`); }

			const rsvp = await serverFetch(`/register/get-register-info.ofer?${params.join('&')}`);
			if (rsvp.status !== 'OK') { return; }

			const { category: cat, location: loc } = rsvp;
			state.backgroundUrl.value = rsvp.backgroundImage || '';

			const customPlaceholder = (rsvp.showSuggestions) ? cat.categoryName : '';

			const { value: regCat } = state.registerCategory;
			regCat.showSuggestions = rsvp.showSuggestions;

			regCat.suggestions = rsvp.suggestions.map((item: BackendCategorySuggestion) => ({
				Value: item.idCategory,
				Label: item.categoryName + (!item.service ? state.notServiceLabel.value : ''),
				Input: item.categoryName,
				Element: item,
			}));

			if (cat) {
				regCat.value = !customPlaceholder ? [cat.idCategory, cat.categoryName] : [];
				regCat.isLeaf = cat.isLeaf;
				regCat.categoryId = {
					level2: cat.idCategoryLevel2,
					level3: cat.idCategoryLevel3,
					parent: cat.idParent,
					id: cat.idCategory,
				};
				regCat.customPlaceholder = cat.isLeaf ? customPlaceholder : '';
			}

			if (loc) {
				state.registerLocation.value.value = [{ ...loc, id: loc.idZip }, loc.city[1]];
			}
		},

		/**
		 * Przygotowuje informacje dotyczące lokalizacji do wysyłki,
		 * w zależności od wyboru Polski lub innego kraju
		 */
		prepareLocationDataForSend(formObject: Record<string, unknown>) {
			const { value: regLoc } = state.registerLocation;
			let loc = regLoc.value[0];

			let location = {};
			if (regLoc.countryMode.enabled) {
				location = {
					'location.country[0]': regLoc.countryMode.value,
					'location.country[1]': regLoc.countryMode.value,
					'location.region[0]': '',
					'location.region[1]': '',
					'location.district[0]': '',
					'location.district[1]': '',
					'location.city[0]': formObject['location.zip'],
					'location.city[1]': formObject['location.city'],
				};
				delete formObject['location.zip'];
				delete formObject['location.city'];
				loc = { id: -1 };
			} else {
				location = {
					'location.country[0]': (loc.country || [])[0] || '',
					'location.country[1]': (loc.country || [])[1] || '',
					'location.region[0]': (loc.region || [])[0] || '',
					'location.region[1]': (loc.region || [])[1] || '',
					'location.district[0]': (loc.district || [])[0] || '',
					'location.district[1]': (loc.district || [])[1] || '',
					'location.city[0]': (loc.city || [])[0] || '',
					'location.city[1]': (loc.city || [])[1] || '',
				};
			}

			return { location, locId: loc.id };
		},

		/**
		 * Wysyła informacje i próbuje założyć konto
		 * @returns Tablica z błędami. Jeżeli jest pusta, znaczy, że konto zostało założone
		 */
		async sendForm(formObject: Record<string, unknown>) {
			// Przygotuj dane do wysyłki
			const { location, locId } = actions.prepareLocationDataForSend(formObject);
			const nip = formObject.nip as NipValue;
			delete formObject['nip'];
			if (state.initRfpId.value) { formObject.idRfp = state.initRfpId.value; }
			if (state.referralCode.value) { formObject.referralCode = state.referralCode.value; }

			const inputData = {
				...formObject,
				...location,
				'idZipSearch': locId,
				'locations': state.selectedAdditionalLocs.value,
				'mainCat': state.registerCategory.value.value[0],
				'cats': state.selectedAdditionalCats.value.map((i) => Number(i)),
				'nip.country': nip.country,
				'nip.value': nip.value,
				'nip.isIndividual': !!nip.value,
			};

			// Wyślij formularz i zwróć wynik akcji
			const rsvp = await serverFetch('/register/sign-in.ofer', inputData, 'POST');
			if (rsvp.status === 'OK') {
				return {
					registrationErrors: [],
					customerId: rsvp.idCustomer,
				};
			}

			return {
				registrationErrors: rsvp.registrationErrors,
				customerId: 0,
			};
		},
	};

	const getters = {};

	return { ...state, ...actions, ...getters };
});
